import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PlusCircle, Trash2, Package } from "lucide-react";
import { AdminBillingSummary } from "@/components/SubscriptionBreakdown";

const CAT_LABELS = {
  users: "Utilisateurs",
  storage: "Stockage",
  org: "Organisation",
  web: "Web",
  rh: "RH",
  finance: "Finance",
  api: "API",
  support: "Support",
  custom: "Sur mesure",
  pack: "Packs",
};

/**
 * Panel Options payantes (Phase 11 v2.0.01).
 * L'Admin ajoute/retire des options depuis le catalogue pour une entreprise donnée.
 * Affiche le prix cumulé total.
 */
export default function OptionsPanel({ entrepriseId }) {
  const [catalog, setCatalog] = useState([]);
  const [subscribed, setSubscribed] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [cycle, setCycle] = useState("monthly");
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get("/admin/options-catalog"),
        api.get(`/admin/entreprises/${entrepriseId}/options`),
      ]);
      setCatalog(c.data || []);
      setSubscribed(s.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [entrepriseId]);

  const subscribe = async () => {
    if (!selectedKey) return;
    try {
      await api.post(`/admin/entreprises/${entrepriseId}/options`, { option_key: selectedKey, quantity, cycle });
      toast.success("Option souscrite");
      setSelectedKey("");
      setQuantity(1);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const unsubscribe = async (key) => {
    if (!window.confirm("Retirer cette option ?")) return;
    try {
      await api.delete(`/admin/entreprises/${entrepriseId}/options/${key}`);
      toast.success("Option retirée");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const total = useMemo(() => {
    let m = 0, a = 0;
    subscribed.forEach((s) => {
      const q = s.quantity || 1;
      if (s.cycle === "annual") a += (s.option_annual || 0) * q;
      else m += (s.option_monthly || 0) * q;
    });
    return { m, a };
  }, [subscribed]);

  const groupedCatalog = useMemo(() => {
    const g = {};
    catalog.forEach((o) => { (g[o.category] = g[o.category] || []).push(o); });
    return g;
  }, [catalog]);

  return (
    <div className="space-y-6" data-testid="options-panel">
      <AdminBillingSummary entrepriseId={entrepriseId} refreshKey={subscribed.length + subscribed.map((s) => `${s.option_key}:${s.quantity}:${s.cycle}`).join("|")} />
      <div className="bg-white border border-[#DEE2E6] p-6">
        <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#868E96] mb-4 flex items-center gap-2">
          <Package className="h-3.5 w-3.5" /> Options souscrites
        </div>

        {loading ? (
          <div className="text-sm text-[#868E96]">Chargement…</div>
        ) : subscribed.length === 0 ? (
          <div className="p-8 border border-dashed border-[#DEE2E6] text-center text-sm text-[#868E96]" data-testid="options-empty">
            Aucune option souscrite pour cette entreprise.
          </div>
        ) : (
          <div className="divide-y divide-[#DEE2E6]">
            {subscribed.map((s) => (
              <div key={s.option_key} className="py-3 flex items-center justify-between gap-4" data-testid={`opt-row-${s.option_key}`}>
                <div className="flex-1">
                  <div className="font-semibold text-sm">{s.option_name || s.option_key}</div>
                  <div className="text-[10px] text-[#868E96] font-mono">{s.option_key} · qté {s.quantity} · {s.cycle}</div>
                </div>
                <div className="text-sm font-mono text-right">
                  <div className="font-bold">
                    {s.cycle === "annual"
                      ? `${((s.option_annual || 0) * (s.quantity || 1)).toFixed(2)} €/an`
                      : `${((s.option_monthly || 0) * (s.quantity || 1)).toFixed(2)} €/mois`}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => unsubscribe(s.option_key)} data-testid={`opt-remove-${s.option_key}`} className="border-[#E60000] text-[#E60000]">
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {subscribed.length > 0 && (
          <div className="mt-4 pt-4 border-t-2 border-[#0A0A0A] flex justify-between items-center" data-testid="options-total">
            <span className="text-xs uppercase tracking-wider font-bold">Total options</span>
            <div className="font-mono text-right">
              {total.m > 0 && <div className="font-display text-xl font-black">{total.m.toFixed(2)} €/mois</div>}
              {total.a > 0 && <div className="font-display text-xl font-black">{total.a.toFixed(2)} €/an</div>}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-[#DEE2E6] p-6" data-testid="options-add-form">
        <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#868E96] mb-4 flex items-center gap-2">
          <PlusCircle className="h-3.5 w-3.5" /> Ajouter une option
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Option</label>
            <Select value={selectedKey} onValueChange={setSelectedKey}>
              <SelectTrigger data-testid="opt-select" className="h-10 mt-1"><SelectValue placeholder="Choisir une option…" /></SelectTrigger>
              <SelectContent>
                {Object.entries(groupedCatalog).map(([cat, opts]) => (
                  <React.Fragment key={cat}>
                    <div className="px-2 py-1 text-[9px] uppercase tracking-wider font-bold text-[#868E96]">{CAT_LABELS[cat] || cat}</div>
                    {opts.map((o) => (
                      <SelectItem key={o.key} value={o.key}>
                        {o.name} — {o.monthly ? `${o.monthly}€/mois` : o.one_shot_from ? `dès ${o.one_shot_from}€` : "sur devis"}
                      </SelectItem>
                    ))}
                  </React.Fragment>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Quantité</label>
            <Input type="number" min="1" value={quantity} onChange={(e) => setQuantity(parseInt(e.target.value) || 1)} data-testid="opt-qty" className="h-10 mt-1" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] font-semibold">Cycle</label>
            <Select value={cycle} onValueChange={setCycle}>
              <SelectTrigger data-testid="opt-cycle" className="h-10 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Mensuel</SelectItem>
                <SelectItem value="annual">Annuel</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={subscribe} disabled={!selectedKey} data-testid="opt-add" className="mt-4 h-10 bg-[#002FA7] text-white">
          <PlusCircle className="h-4 w-4 mr-2" /> Ajouter à l&apos;abonnement
        </Button>
        <p className="text-[11px] text-[#495057] mt-3" data-testid="opt-billing-note">Le gérant est prévenu (email + notification) : le prix de l&apos;option s&apos;ajoute à son abonnement et apparaît dans son espace Facturation.</p>
      </div>
    </div>
  );
}
